import styled from '@emotion/styled'
import React, { FormEvent, useCallback, useContext } from 'react'
import { IconCross, IconSearchTN } from '../../../icons'
import {
    getComponentTheme,
    calcRem,
    breakpoint,
    colors,
} from '../../../__styling'
import { DataLayerEventName, SearchEvent } from '@news-mono/web-common'
import { tokens } from '@news-mono/design-tokens'
import { ElectionFilterContext, ElectionFilterValue } from '../../../contexts'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import debounce from 'lodash.debounce'

type FormProps = {
    onEvent?: (event: SearchEvent) => void
    electionFilter: ElectionFilterValue<string>
}

export const ElectionSearchInput = ({ onEvent, electionFilter }: FormProps) => {
    const { search, submitSearch } = useContext(ElectionFilterContext)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [localSearchTerm, setLocalSearchTerm] = React.useState(
        electionFilter.value,
    )

    React.useEffect(() => {
        setLocalSearchTerm(search.value)
    }, [search.value])

    //Debounced function to avoid multiple calls to update search.setValue
    //when the user types in the input field to prevent performance issues
    const debouncedSetSearchValue = React.useMemo(
        () =>
            debounce((value: string) => {
                search.setValue(value)
            }, 200),
        [search],
    )

    // Cleanup the debounced function on unmount
    React.useEffect(() => {
        return () => {
            debouncedSetSearchValue.cancel()
        }
    }, [debouncedSetSearchValue])

    const handleSubmit = useCallback(
        (e?: FormEvent<HTMLFormElement>, formData?: FormData) => {
            if (e) {
                e.preventDefault()
                formData = new FormData(e.target as HTMLFormElement)
            }

            const inputValue =
                formData?.get('search')?.toString().toLowerCase().trim() || ''

            setLocalSearchTerm(inputValue)
            submitSearch(inputValue)

            // Dismiss the on-screen keyboard on mobile devices
            inputRef.current?.blur()

            onEvent?.({
                type: DataLayerEventName.search,
                originator: 'ElectionSearch',
                payload: {
                    searchTerm: inputValue,
                },
            })
        },
        [onEvent, submitSearch],
    )

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setLocalSearchTerm(value)
        debouncedSetSearchValue(value)

        if (value.trim() === '') {
            search.setValue(search.initialValue)
            submitSearch(search.initialValue)
        }
    }

    return (
        <StyledElectionContainer>
            <StyledElectionForm onSubmit={handleSubmit}>
                <StyledSearchButton type="submit">
                    <IconSearchTN
                        fillColour={
                            tokens.thenightly.colors.palette.neutral[70]
                        }
                    />
                </StyledSearchButton>
                <StyledInput
                    placeholder="Search postcode, electorate or candidate"
                    type="text"
                    name="search"
                    minLength={1}
                    maxLength={100}
                    ref={inputRef}
                    value={localSearchTerm}
                    hasSearchValue={localSearchTerm.trim() !== ''}
                    onChange={handleInputChange}
                />
                <ElectionSearchClearButton
                    type="reset"
                    hasSearchValue={localSearchTerm.trim() !== ''}
                    onClick={() => {
                        setLocalSearchTerm('')
                        search.setValue(search.initialValue)
                        submitSearch(search.initialValue)
                    }}
                >
                    <ElectionSearchCrossIcon label="Clear Search" />
                </ElectionSearchClearButton>
            </StyledElectionForm>
        </StyledElectionContainer>
    )
}

const StyledElectionContainer = styled.div(({ theme }) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        gap: calcRem(8),
    }
})

const StyledElectionForm = styled.form(({ theme }) => {
    const electionTheme = getComponentTheme(theme, 'electionTheme')

    return {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        background: electionTheme.input.backgroundColor,
        color: electionTheme.input.textColor,
        borderRadius: calcRem(8),
        padding: calcRem(8, 16),
        outline: `1px solid ${electionTheme.input.outlineColor}`,
        position: 'relative',

        ['&:is(:hover, :focus-within)']: {
            outline: `1px solid ${electionTheme.input.outlineHoverColor}`,
        },

        transition: 'all 0.2s',
    }
})

const StyledInput = styled.input<{ hasSearchValue: boolean }>(
    ({ theme, hasSearchValue }) => {
        const electionTheme = getComponentTheme(theme, 'electionTheme')

        return {
            background: 'inherit',
            border: 'none',
            outline: 'none',
            flexGrow: 1,
            fontFamily: electionTheme.input.fontFamily,
            fontSize: electionTheme.input.fontSize,
            lineHeight: electionTheme.input.lineHeight,
            letterSpacing: electionTheme.input.letterSpacing,
            height: calcRem(40),
            paddingLeft: calcRem(16),
            paddingRight: hasSearchValue ? calcRem(40) : calcRem(0),
            transition: 'padding-right 0.2s ease-in-out',
            width: '100%',
        }
    },
)

const StyledSearchButton = styled.button(({ theme }) => {
    return {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        display: 'grid',
        placeItems: 'center',
    }
})

interface ElectionClearButtonProps {
    hasSearchValue: boolean
}

const ElectionSearchClearButton = styled('button')<ElectionClearButtonProps>(
    ({ hasSearchValue, theme }) => ({
        position: 'absolute',
        right: calcRem(12),
        top: '50%',
        transform: 'translateY(-50%)',
        padding: calcRem(2),
        background: 'transparent',
        cursor: 'pointer',
        border: 'none',
        lineHeight: 0,
        opacity: hasSearchValue ? 1 : 0,
        visibility: hasSearchValue ? 'visible' : 'hidden',
        transition: 'opacity 0.2s ease-in-out',
        willChange: 'opacity, visibility',

        '&:hover path, &:focus path': {
            fill: theme.sectionValues.primaryColor,
        },
    }),
)

const ElectionCross: React.FC<{ label: string }> = ({ label, ...rest }) => (
    <svg
        {...rest}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-labelledby="svg-title"
    >
        <title id="svg-title">{label}</title>
        <path
            d="M18 6L6 18M6 6L18 18"
            stroke="#525252"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
    </svg>
)

const ElectionSearchCrossIcon = styled(ElectionCross)({
    height: calcRem(24),
    width: calcRem(24),
    fill: colors.thewest.greyGorilla,
})
